import React, { useEffect, useState } from 'react';
import './BuyCreditsPage.css';

const baseURL = process.env.REACT_APP_API_BASE_URL;
const API_KEY  = process.env.REACT_APP_BROWSER_API_KEY;

const CHECK = (
  <svg className="bcp-check" viewBox="0 0 20 20" fill="none">
    <path d="M5 10.5l3.5 3.5 6.5-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SLAB_META = [
  { name: 'Starter',    label: 'Pack', icon: '📷', btnClass: 'bcp-btn-blue',   cardClass: 'bcp-card--starter' },
  { name: 'Basic',      label: 'Pack', icon: '⚡', btnClass: 'bcp-btn-indigo',  cardClass: 'bcp-card--basic'   },
  { name: 'Growth',     label: 'Pack', icon: '👑', btnClass: 'bcp-btn-orange',  cardClass: 'bcp-card--growth'  },
  { name: 'Scale',      label: 'Pack', icon: '🚀', btnClass: 'bcp-btn-purple',  cardClass: 'bcp-card--purple'  },
  { name: 'Enterprise', label: 'Pack', icon: '↗',  btnClass: 'bcp-btn-green',   cardClass: 'bcp-card--scale'   },
];

const SLAB_FEATURES = [
  ['AI Face Recognition', 'Personalized Photo Delivery', 'Lifetime Archived Storage', 'Watermark (Easy Events)', 'Download: Configurable'],
  ['Everything in Starter', 'Faster AI Processing', 'Priority Upload Queue', 'Bulk Upload Stability', 'Watermark (Easy Events)'],
  ['Everything in Basic', 'Better Compression Optimization', 'High-Volume Processing', 'Dedicated Processing Pipeline', 'Share: Configurable'],
  ['Everything in Growth', 'Optimized Storage Handling', 'Large Event Support', 'Dedicated Processing Pipeline', 'Priority Support'],
  ['Everything in Scale', 'Maximum Throughput', 'Dedicated Pipeline', 'Optimized Storage', 'Enterprise Support'],
];

const fmt    = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n);
const fmtNum = (n) => new Intl.NumberFormat('en-IN').format(n);

/* ─── Purchase Step ─────────────────────────────────────────────────── */
function PurchaseStep({ slab, slabMeta, gstRate, onBack, onClose, onSuccess, phone }) {
  const isUnbounded = slab.max_credits >= 2147483647;
  const [qty,             setQty]             = useState(String(slab.min_credits));
  const [touched,         setTouched]         = useState(false);
  const [paying,          setPaying]          = useState(false);
  const [apiError,        setApiError]        = useState(null);
  const [success,         setSuccess]         = useState(false);
  const [couponCode,      setCouponCode]      = useState('');
  const [couponApplied,   setCouponApplied]   = useState(null);
  const [couponError,     setCouponError]     = useState(null);
  const [couponValidating, setCouponValidating] = useState(false);

  const parsed = parseInt(qty, 10);
  const valid  = !isNaN(parsed)
    && parsed >= slab.min_credits
    && (isUnbounded || parsed <= slab.max_credits);

  const subtotal       = valid ? parsed * slab.price_per_credit : 0;
  const couponData     = couponApplied?.couponsData;
  const discountAmount = couponData && valid
    ? couponData.type === 'percentage'
      ? (subtotal * couponData.value) / 100
      : couponData.value
    : 0;
  const discountedSubtotal = subtotal - discountAmount;
  const gst   = (discountedSubtotal * gstRate) / 100;
  const total = discountedSubtotal + gst;

  const handleApplyCoupon = async () => {
    const code = couponCode.trim();
    if (!code) { setCouponError('Please enter a coupon code.'); return; }
    try {
      setCouponValidating(true);
      setCouponError(null);
      setCouponApplied(null);
      const res = await fetch(`${baseURL}/photographer/validate-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify({ coupon_code: code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.errorMessage || 'Invalid coupon code.');
      setCouponApplied({ ...data, code });
    } catch (err) {
      setCouponError(err.message || 'Invalid coupon code.');
    } finally {
      setCouponValidating(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponApplied(null);
    setCouponCode('');
    setCouponError(null);
  };

  const hintText = isUnbounded
    ? `Minimum ${fmtNum(slab.min_credits)} credits · 1 credit = 1 photo`
    : `Enter between ${fmtNum(slab.min_credits)} – ${fmtNum(slab.max_credits)} credits · 1 credit = 1 photo`;

  const errorMsg = touched && !valid
    ? isUnbounded
      ? `Minimum ${fmtNum(slab.min_credits)} credits required`
      : `Enter a value between ${fmtNum(slab.min_credits)} and ${fmtNum(slab.max_credits)}`
    : null;

  const loadRazorpayScript = () =>
    new Promise((resolve) => {
      if (window.Razorpay) { resolve(true); return; }
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload  = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });

  const handlePay = async () => {
    if (!valid || paying) return;
    const vendorId = localStorage.getItem('vendorId');
    if (!vendorId) { setApiError('Vendor not found. Please log in again.'); return; }

    try {
      setPaying(true);
      setApiError(null);

      // 1. Create Razorpay order via API
      const buyPayload = { vendor_id: vendorId, slab_id: slab.slab_id, credit_count: parsed };
      if (couponApplied?.code) buyPayload.coupon_code = couponApplied.code;
      const res = await fetch(`${baseURL}/photographer/buy-credits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': API_KEY },
        body: JSON.stringify(buyPayload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.errorMessage || 'Purchase failed');

      // Direct payment — no Razorpay needed
      if (data.payment_type === 'direct') {
        if (onSuccess) onSuccess();
        setPaying(false);
        setSuccess(true);
        return;
      }

      const { order_id, razorpay_key_id, amount, currency } = data;

      // 2. Load Razorpay checkout script
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error('Failed to load payment gateway. Check your connection.');

      // 3. Open Razorpay checkout
      const options = {
        key: razorpay_key_id,
        amount,
        currency,
        order_id,
        name: 'Easy Events',
        description: `${fmtNum(parsed)} Photo Credits`,
        prefill: { contact: phone || '' },
        theme: { color: '#F80353' },
        handler: () => {
          if (onSuccess) onSuccess();
          setPaying(false);
          setSuccess(true);
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => {
        setPaying(false);
        setApiError(resp?.error?.description || 'Payment failed. Please try again.');
      });
      rzp.open();

    } catch (err) {
      setApiError(err.message || 'Something went wrong. Please try again.');
      setPaying(false);
    }
  };

  if (success) {
    return (
      <div className="bcp-purchase bcp-success">
        <div className="bcp-success-icon">🎉</div>
        <h2 className="bcp-success-title">Credits Added!</h2>
        <p className="bcp-success-msg">
          <strong>{fmtNum(parsed)} credits</strong> have been added to your account.
        </p>
        <button className="bcp-pay-btn bcp-btn-green" onClick={onClose}>
          Done
        </button>
      </div>
    );
  }

  return (
    <div className="bcp-purchase">
      {/* Header */}
      <div className="bcp-purchase-header">
        <button className="bcp-back-btn" onClick={onBack} disabled={paying}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Plans
        </button>

        <div className="bcp-purchase-plan-badge">
          <span>{slabMeta.icon}</span>
          <span className="bcp-purchase-plan-name">{slabMeta.name} {slabMeta.label}</span>
          <span className="bcp-purchase-plan-price">₹{slab.price_per_credit} / photo</span>
        </div>
      </div>

      {/* Quantity input */}
      <div className="bcp-qty-section">
        <label className="bcp-qty-label">How many credits do you need?</label>
        <div className={`bcp-qty-wrap${errorMsg ? ' bcp-qty-wrap--error' : ''}`}>
          <input
            className="bcp-qty-input"
            type="number"
            min={slab.min_credits}
            max={isUnbounded ? undefined : slab.max_credits}
            value={qty}
            onChange={(e) => { setQty(e.target.value); setTouched(true); setApiError(null); }}
            onBlur={() => setTouched(true)}
            placeholder={fmtNum(slab.min_credits)}
            disabled={paying}
          />
          <span className="bcp-qty-unit">credits</span>
        </div>
        {errorMsg
          ? <p className="bcp-qty-error">{errorMsg}</p>
          : <p className="bcp-qty-hint">{hintText}</p>
        }
      </div>

      {/* Coupon code */}
      <div className="bcp-coupon-section">
        <label className="bcp-qty-label">Have a coupon code?</label>
        {couponApplied ? (
          <div className="bcp-coupon-applied">
            <span className="bcp-coupon-tag">
              {couponApplied.code}
              {couponData?.type === 'percentage'
                ? ` — ${couponData.value}% off`
                : couponData?.type === 'flat'
                  ? ` — ${fmt(couponData.value)} off`
                  : ' — Applied'}
            </span>
            <button className="bcp-coupon-remove" onClick={handleRemoveCoupon} disabled={paying}>
              Remove
            </button>
          </div>
        ) : (
          <div className="bcp-coupon-wrap">
            <input
              className="bcp-coupon-input"
              type="text"
              placeholder="Enter coupon code"
              value={couponCode}
              onChange={(e) => { setCouponCode(e.target.value.toUpperCase()); setCouponError(null); }}
              onKeyDown={(e) => e.key === 'Enter' && handleApplyCoupon()}
              disabled={couponValidating || paying}
            />
            <button
              className="bcp-coupon-apply-btn"
              onClick={handleApplyCoupon}
              disabled={couponValidating || paying || !couponCode.trim()}
            >
              {couponValidating ? <span className="bcp-pay-spinner" /> : 'Apply'}
            </button>
          </div>
        )}
        {couponError && <p className="bcp-qty-error">{couponError}</p>}
      </div>

      {/* Price breakdown */}
      <div className="bcp-breakdown">
        <div className="bcp-breakdown-row">
          <span>Credits</span>
          <span>{valid ? fmtNum(parsed) : '—'}</span>
        </div>
        <div className="bcp-breakdown-row">
          <span>Price per credit</span>
          <span>₹{slab.price_per_credit.toFixed(2)}</span>
        </div>
        <div className="bcp-breakdown-row">
          <span>Subtotal</span>
          <span>{valid ? fmt(subtotal) : '—'}</span>
        </div>
        {couponData && valid && discountAmount > 0 && (
          <div className="bcp-breakdown-row bcp-breakdown-discount">
            <span>
              Coupon ({couponApplied.code}
              {couponData.type === 'percentage' ? ` ${couponData.value}%` : ''})
            </span>
            <span>− {fmt(discountAmount)}</span>
          </div>
        )}
        <div className="bcp-breakdown-row">
          <span>GST ({gstRate}%)</span>
          <span>{valid ? fmt(gst) : '—'}</span>
        </div>
        <div className="bcp-breakdown-divider" />
        <div className="bcp-breakdown-row bcp-breakdown-total">
          <span>Total Payable</span>
          <span>{valid ? fmt(total) : '—'}</span>
        </div>
      </div>

      {apiError && <p className="bcp-api-error">⚠️ {apiError}</p>}

      <button
        className={`bcp-pay-btn ${slabMeta.btnClass}`}
        disabled={!valid || paying}
        onClick={handlePay}
      >
        {paying
          ? <><span className="bcp-pay-spinner" /> Processing…</>
          : `Proceed to Pay ${valid ? fmt(total) : ''}`
        }
      </button>

      <p className="bcp-pay-note">Charges apply only on successful uploads. Failed uploads are not charged.</p>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────────────── */
export default function BuyCreditsPage({ onClose, onSuccess, phone }) {
  const [slabs,       setSlabs]       = useState([]);
  const [meta,        setMeta]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [selectedSlabIdx, setSelectedSlabIdx] = useState(null); // null = show plan list

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${baseURL}/photographer/plans`, {
          headers: { 'x-api-key': API_KEY },
        });
        if (!res.ok) throw new Error('Failed to fetch plans');
        const data  = await res.json();
        const plans = data.plans || {};
        setMeta({ displayName: plans.display_name, description: plans.description, gstRate: plans.gst_rate });
        setSlabs(plans.slabs || []);
      } catch (err) {
        setError(err.message || 'Could not load plans');
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, []);

  const popularIdx = Math.floor(slabs.length / 2);

  return (
    <div className="bcp-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bcp-sheet">
        <button className="bcp-close" onClick={onClose} aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {/* ── Purchase Step ── */}
        {selectedSlabIdx !== null ? (
          <PurchaseStep
            slab={slabs[selectedSlabIdx]}
            slabMeta={SLAB_META[selectedSlabIdx] || SLAB_META[SLAB_META.length - 1]}
            gstRate={meta?.gstRate ?? 18}
            onBack={() => setSelectedSlabIdx(null)}
            onClose={onClose}
            onSuccess={onSuccess}
            phone={phone}
          />
        ) : (
          <>
            {/* ── Hero ── */}
            <div className="bcp-hero">
              <h1 className="bcp-title">{meta?.displayName || 'Choose Your Upload Plan'}</h1>
              <p className="bcp-subtitle">
                {meta?.description || 'Upload, organize & deliver event photos with AI-powered face recognition and lifetime memory storage.'}
              </p>
            </div>

            {loading && (
              <div className="bcp-loader">
                <div className="bcp-spinner" />
                <span>Loading plans…</span>
              </div>
            )}

            {error && !loading && (
              <div className="bcp-error">⚠️ {error}</div>
            )}

            {/* ── Plan Cards ── */}
            {!loading && !error && (
              <div className="bcp-cards">
                {slabs.map((slab, idx) => {
                  const m        = SLAB_META[idx] || SLAB_META[SLAB_META.length - 1];
                  const features = SLAB_FEATURES[idx] || SLAB_FEATURES[SLAB_FEATURES.length - 1];
                  const isPopular = idx === popularIdx;
                  const maxLabel  = slab.max_credits >= 2147483647
                    ? `${(slab.min_credits / 1000).toFixed(0)}K+ Photos`
                    : slab.display_label;

                  return (
                    <div key={slab.slab_id} className={`bcp-card ${m.cardClass}${isPopular ? ' bcp-card--popular-ring' : ''}`}>
                      {isPopular && <div className="bcp-popular-badge">Most Popular</div>}

                      <div className="bcp-card-header">
                        <span className="bcp-plan-icon">{m.icon}</span>
                        <h2 className="bcp-plan-name">{m.name} <span>{m.label}</span></h2>
                      </div>

                      <div className="bcp-price-row">
                        <span className="bcp-price">₹{slab.price_per_credit}</span>
                        <span className="bcp-per">/ photo + GST</span>
                      </div>
                      {meta?.gstRate && <p className="bcp-gst-note">+{meta.gstRate}% GST applicable</p>}
                      <p className="bcp-photos">{maxLabel}</p>

                      <hr className="bcp-divider" />

                      <ul className="bcp-features">
                        {features.map((f) => (
                          <li key={f}>{CHECK}{f}</li>
                        ))}
                      </ul>

                      <button
                        className={`bcp-buy-btn ${m.btnClass}`}
                        onClick={() => setSelectedSlabIdx(idx)}
                      >
                        Buy Credits
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Info Cards ── */}
            <div className="bcp-info-row">
              <div className="bcp-info-card bcp-info-card--pricing">
                <h3>How Pricing Works</h3>
                <ul>
                  <li>{CHECK} 1 photo upload = 1 credit</li>
                  <li>{CHECK} Credits are prepaid</li>
                  <li>{CHECK} Charges apply only on successful uploads. Failed uploads are not charged.</li>
                </ul>
              </div>
              <div className="bcp-info-card bcp-info-card--smart">
                <h3>Smart Photo Experience</h3>
                <ul>
                  <li>{CHECK} AI automatically finds attendee photos</li>
                  <li>{CHECK} Users see their photos first</li>
                  <li>{CHECK} Photos are organized into a clean event feed</li>
                  <li>{CHECK} Privacy first visibility system</li>
                </ul>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
